import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'guide';
  const [isChanging, setIsChanging] = useState(false);

  const currentCategory = categories.find((c) => c.slug === activeTab);
  const categoryArticles = getArticlesByCategory(activeTab);

  const handleTabChange = (slug: string) => {
    setIsChanging(true);
    setSearchParams({ tab: slug });
    setTimeout(() => setIsChanging(false), 200);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const currentCategoryIndex = categories.findIndex((c) => c.slug === activeTab);

  return (
    <main className="min-h-screen pt-16">
      <div className="bg-background-100 py-16 md:py-20">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-serif text-foreground-900 mb-4">
            分类浏览
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
            选择您感兴趣的分类，浏览91n相关文章内容
          </p>
          <p className="text-xs text-foreground-400 mt-2">
            当前浏览人数 2,358 人 · 今日更新 3 篇
          </p>
        </div>
      </div>

      <div className="sticky top-16 z-40 bg-background-50 border-b border-background-200">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleTabChange(cat.slug)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeTab === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className={`${cat.icon} text-sm`} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-10">
        {currentCategory && (
          <div className="mb-8">
            <h3 className="text-xl font-bold font-serif text-foreground-900 mb-2">
              {currentCategory.name}
            </h3>
            <p className="text-sm text-foreground-500">
              {currentCategory.description}
              <span className="text-foreground-300 ml-2">
                · 共 {categoryArticles.length} 篇文章
              </span>
            </p>
          </div>
        )}

        <div className={`transition-opacity duration-200 ${isChanging ? 'opacity-50' : 'opacity-100'}`}>
          {categoryArticles.length > 0 ? (
            <div className="flex flex-col gap-4">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="flex flex-col sm:flex-row gap-5 p-5 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors duration-200 cursor-pointer group"
                >
                  <div className="w-full sm:w-56 h-36 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={`${article.title} - 91n`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2 flex-wrap">
                      <span>{article.publishDate}</span>
                      <span>·</span>
                      <span>{article.readTime} 分钟阅读</span>
                      <span>·</span>
                      <span>{article.author}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-xs text-primary-500 font-medium">
                      阅读全文
                      <i className="ri-arrow-right-line" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-inbox-line text-2xl" />
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </div>

      <nav className="w-full max-w-6xl mx-auto px-4 md:px-6 pb-10 flex items-center justify-center gap-2">
        <span className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium">1</span>
        <span className="px-3 py-1.5 rounded-md text-foreground-400 text-sm">2</span>
        <span className="px-3 py-1.5 rounded-md text-foreground-400 text-sm">3</span>
        <span className="px-3 py-1.5 text-foreground-400 text-sm">...</span>
        <span className="px-3 py-1.5 rounded-md text-foreground-400 text-sm">8</span>
      </nav>
    </main>
  );
}